import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { AUTHORIZATION_DETAILS, BACK_TO, INSTANCE_NAME } from './verifyOAuthParams';

interface AuthorizationDetail {
  identifier: string;
  type: string;
}

function isAuthorizationDetail(value: unknown): value is AuthorizationDetail {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).identifier === 'string' &&
    typeof (value as Record<string, unknown>).type === 'string'
  );
}

function isAuthorizationDetailsArray(value: unknown): value is AuthorizationDetail[] {
  return Array.isArray(value) && value.every(isAuthorizationDetail);
}

interface UseAuthorizationDetailsResult {
  instanceName: string | null;
  backTo: string | null;
  authorizationDetails: AuthorizationDetail[] | null;
}

const emptyResponse: UseAuthorizationDetailsResult = { instanceName: null, backTo: null, authorizationDetails: null };

export function useAuthorizationDetails(): UseAuthorizationDetailsResult {
  const { search } = useLocation();
  const raw = new URLSearchParams(search).get(AUTHORIZATION_DETAILS);

  return useMemo(() => {
    if (!raw) {
      return emptyResponse;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return emptyResponse;
    }

    if (!isAuthorizationDetailsArray(parsed)) {
      return emptyResponse;
    }

    const instanceNameEntry = parsed.find(detail => detail.type === INSTANCE_NAME);

    const backToEntry = parsed.find(detail => detail.type === BACK_TO);

    return {
      instanceName: instanceNameEntry?.identifier ?? null,
      backTo: backToEntry?.identifier ?? null,
      authorizationDetails: parsed,
    };
  }, [raw]);
}
